import {
  decodeFilters,
  decodeSort,
  encodeFilters,
  encodeSort,
  type ListColumnSpec,
  type ListFilter,
  type ListSort,
} from "@patronix/domain/list";
import type { ListView } from "@/components/DataList";

// Column filters and sort of a list screen in the URL (docs/design/column-filters.md):
//   ?f=status:in:draft,released;amount:ge:1000&s=-amount
// One set of helpers for every list route: validate the search params,
// turn them into the RPC's filters / sort, and build the DataList view.

export interface ViewSearch {
  /** Column filters (encodeFilters). */
  f?: string | undefined;
  /** Sort, "-col" descending. */
  s?: string | undefined;
}

/** The valid part of ?f= and ?s= (for validateSearch); drops what the spec does not allow. */
export function viewSearch(spec: readonly ListColumnSpec[], raw: Record<string, unknown>): ViewSearch {
  const out: ViewSearch = {};
  const f = encodeFilters(decodeFilters(spec, raw.f));
  if (f) out.f = f;
  const s = encodeSort(decodeSort(spec, raw.s));
  if (s) out.s = s;
  return out;
}

/** Filters and sort for the list query (part of its query key). */
export function viewQuery(spec: readonly ListColumnSpec[], search: ViewSearch): { filters: ListFilter[]; sort: ListSort | null } {
  return { filters: decodeFilters(spec, search.f), sort: decodeSort(spec, search.s) };
}

/** DataList view: menus read the URL and write it back. */
export function listView(
  spec: readonly ListColumnSpec[],
  search: ViewSearch,
  setSearch: (patch: ViewSearch) => void,
): ListView {
  return {
    spec,
    query: viewQuery(spec, search),
    onChange: (next) => setSearch({ f: encodeFilters(next.filters) || undefined, s: encodeSort(next.sort) || undefined }),
  };
}

/** Clears filters and sort (the "clear filters" action of an empty list). */
export const noView: ViewSearch = { f: undefined, s: undefined };
